import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, URGENCY, MODALITY } from "@/lib/api";
import { useSeo } from "@/lib/draft";
import { Loading } from "@/App";
import { StageChip } from "@/pages/MinhasSolicitacoes";

export default function SolicitacaoDetalhe() {
  useSeo("Acompanhar solicitação", null, true);
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const load = useCallback(() => api.get(`/me/requests/${id}`).then((x) => setR(x.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/me/requests/${id}/messages`, { text });
      setText("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (err) return <p className="wrap py-12 text-red-700" data-testid="request-detail-error">{err}</p>;
  if (!r) return <Loading />;
  return (
    <div className="wrap max-w-4xl py-10" data-testid="request-detail">
      <Link to="/minhas-solicitacoes" className="btn-ghost -ml-3"><ArrowLeft size={16} />Minhas solicitações</Link>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-2xl font-extrabold text-[#1465C8] md:text-3xl" data-testid="detail-protocol">{r.protocol}</h1>
        <StageChip stage={r.stage} label={r.stage_label} />
      </div>
      <p className="mt-2 text-sm text-[#64748B]">O status representa a situação administrativa da solicitação e não indica aceitação de representação profissional, salvo quando formalizada.</p>
      {r.stage === "aguardando_info" && <p className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900" data-testid="awaiting-info-banner">Precisamos de informações complementares. Responda pela área de comunicações abaixo.</p>}
      <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-6">
          <section className="card">
            <h2 className="text-lg font-extrabold">{r.category_name}</h2>
            <p className="mt-1 text-xs text-[#64748B]">{MODALITY[r.modality]} · Urgência {URGENCY[r.urgency]} · Enviada em {fmtDate(r.created_at)}</p>
            {r.services?.length > 0 && <p className="mt-3 text-sm"><strong>Serviços:</strong> {r.services.join(", ")}</p>}
            {r.company && <p className="mt-1 text-sm"><strong>Empresa:</strong> {r.company.name}</p>}
            <p className="mt-4 whitespace-pre-wrap break-words">{r.description}</p>
          </section>
          <section className="card" data-testid="user-messages">
            <h2 className="text-lg font-extrabold">Comunicações</h2>
            <div className="mt-4 space-y-3">
              {r.messages.length === 0 && <p className="text-sm text-[#64748B]">Nenhuma mensagem ainda.</p>}
              {r.messages.map((m) => (
                <div key={m.message_id} className={`rounded-xl p-3 text-sm ${m.author_role === "admin" ? "bg-[#EDF5FF]" : "ml-8 bg-[#1465C8] text-white"}`}>
                  <p className="whitespace-pre-wrap">{m.text}</p><p className="mt-1 text-[11px] opacity-70">{m.author_name} · {fmtDateTime(m.created_at)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <textarea className="field min-h-[48px] flex-1" rows={2} placeholder="Complementar informações..." value={text} onChange={(e) => setText(e.target.value)} data-testid="user-message-input" />
              <button className="btn-primary px-4" onClick={send} aria-label="Enviar" data-testid="user-message-send-btn"><Send size={18} /></button>
            </div>
          </section>
        </div>
        <aside className="space-y-6">
          <section className="card" data-testid="user-history">
            <h2 className="text-lg font-extrabold">Andamento</h2>
            <ol className="mt-4 space-y-4 border-l-2 border-[#3198EF]/30 pl-4">
              {r.history.map((h, i) => (
                <li key={i} className="relative"><span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-[#1465C8]" /><p className="font-semibold">{h.stage_label}</p><p className="text-xs text-[#64748B]">{fmtDateTime(h.created_at)}</p></li>
              ))}
            </ol>
          </section>
          <section className="card">
            <h2 className="text-lg font-extrabold">Documentos</h2>
            {r.documents.length === 0 ? <p className="mt-2 text-sm text-[#64748B]">Nenhum documento disponibilizado.</p> :
              r.documents.map((d) => <p key={d.document_id} className="mt-2 text-sm">{d.name}</p>)}
          </section>
        </aside>
      </div>
    </div>
  );
}
